import React, { useCallback, useEffect, useState } from 'react'
import Module from '../../../../layout/Module'
import { Box } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import useClasses from '../../../../../hooks/classes/useClasses'
import AlertSaveButton from '../../../../button/AlertSaveButton'
import { getClassById } from '../../../../../services/classes/classes.service'
import {
  updateClassForm,
  updateClassFormField,
  updateClassPreview,
  updateClassFormError,
  updateClassDialog
} from '../../../../../store/actions/class.actions'
import { handlerErrorV2 } from '../../../../../utils/errors'
import { Alert } from '@mui/material'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import { toggleAlertModule } from '../../../../../store/actions/alert.actions'
import CardsBasicInformation from '../CardsBasicInformation/CardsBasicInformation'
import FormUsers from '../FormUsers/FormUsers'
import CardKeyValue from '../../../../card/cardKeyValue'
import { updateFilter } from '../../../../../store/actions/filter.actions'
import { setCurrentCurriculum } from '../../../../../store/actions/assessment.actions'

const Edit = () => {
  const stepsAllowed = ['add-students', 'remove-students', 'add-teachers', 'remove-teachers']
  const dispatch = useDispatch()
  const { curriculumId, id, step, studentId } = useParams()
  const formData = useSelector((state) => state?.class?.form || {})
  const permission = useSelector((state) => state?.session?.permissions?.class || {})
  const alertSave = useSelector((state) => state?.alert?.save || {})
  const redirectStudentId = studentId ? `${studentId}/` : ''
  const basepath = `/dashboard/${redirectStudentId}classes/${curriculumId}`
  const { addUsersToClass, removeUsersFromClass, isLoading } = useClasses()

  const [error, setError] = useState(null)
  const navigate = useNavigate()

  const [loading, toggleLoading] = useState(false)
  const [isLoadingSave, toggleIsLoadingSave] = useState(false)

  const onCancel = () => {
    navigate(basepath)
    dispatch(updateFilter('class', {}))
  }

  const onChangeAlert = (key, value) => {
    dispatch(updateClassFormField(key, value))
    if (!alertSave.mode || !alertSave.onSave) {
      dispatch(toggleAlertModule(false, 'Classes', 'edit', onSave, alertSave.onDontSave))
    }
  }

  const onSave = async (e) => {
    setError(null)
    e.preventDefault()
    try {
      if (!stepsAllowed.includes(step)) {
        setError({ message: 'Invalid step' })
      }

      toggleIsLoadingSave(true)
      let result = { error: false }
      const userTypes = step.split('-')[1]

      if (step.indexOf('add') > -1) {
        const form = {
          _id: formData._id,
          [userTypes]: formData?.add?.map((item) => item._id) || []
        }
        result = await addUsersToClass(curriculumId, id, step, form)
      }
      if (step.indexOf('remove') > -1) {
        const form = {
          _id: formData._id,
          [userTypes]: formData?.remove?.map((item) => item._id) || []
        }
        result = await removeUsersFromClass(curriculumId, id, step, form)
      }
      if (result.error) {
        if (result.message) {
          setError({ message: result.message })
        } else {
          dispatch(updateClassFormError(result))
        }
      } else {
        onCancel()
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setError({ message })
    } finally {
      toggleIsLoadingSave(false)
    }
  }

  const onLoad = useCallback(async () => {
    if (id && id !== 'batch') {
      toggleLoading(true)
      try {
        const response = await getClassById(curriculumId, id)
        if (!response?.data && response?.error) {
          setError({
            message: response.error.message || 'An error ocurred'
          })
        } else {
          const formData = response?.data || {}
          dispatch(updateClassForm(formData))
          if (formData?.curriculumId) {
            dispatch(setCurrentCurriculum(formData?.curriculumId))
          }
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        setError(message)
      } finally {
        toggleLoading(false)
      }
    }
  }, [curriculumId, id])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  return (
    <>
      <Module.BodyHeader className='border-b border-b-2 border-primary flex justify-between'>
        <div />
        <div className='flex items-center justify-end gap-2'>
          {stepsAllowed.includes(step) && id !== 'batch' && (
            <>
              {permission.archived && (
                <IconTooltipButton
                  id={`archive-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Archive'
                  icon='archive'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--destructive)',
                    color: '#fff',
                    padding: '0.45rem'
                  }}
                  className='shadow-sm'
                  onClick={() => dispatch(updateClassDialog(true, 'archive', formData))}
                />
              )}
              {permission.deleted && (
                <IconTooltipButton
                  id={`delete-${id}`}
                  disabled={loading || isLoadingSave || isLoading || error?.message}
                  title='Delete'
                  icon='trash-alt'
                  style={{
                    borderRadius: '0.5rem',
                    backgroundColor: '#fff',
                    color: 'var(--destructive)',
                    padding: '0.45rem'
                  }}
                  className='shadow-sm'
                  onClick={() => dispatch(updateClassDialog(true, 'delete', formData))}
                />
              )}
              <div className='px-1' />
            </>
          )}
          <AlertSaveButton
            submodule='Classes'
            disabled={loading || isLoadingSave || isLoading}
            icon={['fal', 'times']}
            title='Cancel'
            color='primary'
            variant='outlined'
            className='shadow-sm w-full'
            onDontSave={onCancel}
          />
          {permission.updated && stepsAllowed.includes(step) && (
            <ButtonStartIcon
              disabled={loading || isLoadingSave || isLoading || error?.message}
              loading={isLoadingSave}
              onClick={onSave}
              startIcon={['fal', 'check']}
              color='primary'
              className='w-full'
            >
              Save
            </ButtonStartIcon>
          )}
        </div>
      </Module.BodyHeader>
      <Module.BodyContainer
        loading={isLoadingSave || isLoading}
        classNameWrapper={isLoadingSave || isLoading || loading ? '' : 'overflow-auto'}
      >
        {error?.message && (
          <Alert className='absolute left-0 right-0 z-12' severity='error' onClose={() => setError(null)}>
            {error?.message}
          </Alert>
        )}
        {!loading && (
          <Box className='absolute inset-0'>
            {stepsAllowed.includes(step) && id !== 'batch' && (
              <IconTooltipButton
                disabled={loading}
                title='Preview'
                icon='expand-alt'
                classNameIcon='text-xl'
                style={{
                  padding: '0.45rem',
                  backgroundColor: 'var(--primary)',
                  color: 'var(--primary-foreground)',
                  position: 'absolute',
                  width: 35,
                  height: 35
                }}
                className='shadow-sm  z-10 right-0 top-0'
                onClick={() => dispatch(updateClassPreview('edit'))}
              />
            )}
            {stepsAllowed.includes(step) && id !== 'batch' && <CardsBasicInformation />}
            {stepsAllowed.includes(step) && id === 'batch' && (
              <div className='flex flex-wrap gap-1 pb-4 items-center justify-center'>
                {formData?.classes?.map((item, index) => (
                  <CardKeyValue
                    key={index}
                    title='Class'
                    className={`min-w-100px max-w-150px`}
                    value={<div>{item.code}</div>}
                  />
                ))}
              </div>
            )}
            {stepsAllowed.includes(step) && step.indexOf('teachers') > -1 && (
              <FormUsers
                isLoading={isLoading || isLoadingSave || loading}
                userType={'teachers'}
                onChangeAlert={onChangeAlert}
                type={step}
              />
            )}
            {stepsAllowed.includes(step) && step.indexOf('students') > -1 && (
              <FormUsers
                isLoading={isLoading || isLoadingSave || loading}
                userType={'students'}
                onChangeAlert={onChangeAlert}
                type={step}
              />
            )}
          </Box>
        )}
      </Module.BodyContainer>
    </>
  )
}

export default Edit
